import { Listbox } from '@headlessui/react';
import { memo, useMemo } from 'react';
import { useTheme } from 'styled-components';

import SelectedOptionSvg from '@/shared/assets/icons/ui-kit/option-selected.svg';
import { transformAttribute } from '@/shared/lib/helpers/transform-attribute.helper';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';
import { Box } from '@/shared/ui/box';
import { Icon } from '@/shared/ui/icon';
import { LongText } from '@/shared/ui/long-text';
import { Text } from '@/shared/ui/text';

import * as Styled from './select.styles';
import { SelectProps } from './select.types';

const SelectComponent = <T extends string>({
	options,
	label,
	selected,
	className,
	variant = 'primary',
	onChange,
	Adornment,
	paragraph = '0',
	attachment = '',
	width = 'full',
	minLabelLength = 6,
	disabled = false,
	emptyValueText = '',
	withoutTooltip = false,
	selectedOptionIcon = true,
	required,
	...props
}: SelectProps<T>) => {
	const { select } = useTheme();
	const isMobileAndLaptop = useMatchMedia('mobileAndLaptop');
	const selectedLabel = useMemo(
		() => options.find(({ value }) => value == selected)?.label,
		[options, selected, selectedOptionIcon],
	);

	const renderOptions = useMemo(() => {
		return options.map(({ value, label }) => (
			<Styled.Option key={value} value={value}>
				{({ selected }) =>
					selected ? (
						<Styled.Selected selected>
							{selectedOptionIcon && (
								<Styled.CheckIconWrapper>
									<Icon Svg={SelectedOptionSvg} fill="currentColor" />
								</Styled.CheckIconWrapper>
							)}
							<Styled.LabelOption>{label}</Styled.LabelOption>
						</Styled.Selected>
					) : (
						<Styled.LabelOption>{label}</Styled.LabelOption>
					)
				}
			</Styled.Option>
		));
	}, [options]);

	return (
		<Box className={className} direction="column" position="relative" marginBottom={paragraph}>
			<Listbox disabled={disabled} value={selected} onChange={onChange}>
				{({ open }) => (
					<>
						{label && (
							<Styled.Label
								label={label}
								Component={
									required && (
										<>
											{label}
											<sup>*</sup>
										</>
									)
								}
							/>
						)}
						{Adornment && (
							<Styled.Adornment variant={variant} open={open} disabled={disabled}>
								<Icon Svg={Adornment} />
							</Styled.Adornment>
						)}
						<Styled.Relative>
							<Styled.Select
								adornment={transformAttribute(Boolean(Adornment))}
								variant={variant}
								disabled={disabled}
								width={width}
								hasValue={Boolean(selectedLabel)}
								{...props}
							>
								<Box>
									{withoutTooltip ? (
										<Text label={selectedLabel ? attachment + selectedLabel : emptyValueText} color="inherit" />
									) : (
										<LongText
											label={selectedLabel ? attachment + selectedLabel : emptyValueText}
											length={isMobileAndLaptop ? minLabelLength : 30}
										/>
									)}
								</Box>

								<Styled.SelectArrow position="absolute" open={open} disabled={disabled}>
									<Icon Svg={select.arrow} />
								</Styled.SelectArrow>
							</Styled.Select>
							<Styled.Options scroll={transformAttribute(Object.values(options).length > 5)} variant={variant}>
								{renderOptions}
							</Styled.Options>
						</Styled.Relative>
					</>
				)}
			</Listbox>
		</Box>
	);
};

export default memo(SelectComponent) as typeof SelectComponent;
